"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";

/* ================= IMAGES ================= */
import gearBackgroundImg from "../../assets/images/expedition-gear/backgroundImage.png";
import gearImgHero from "../../assets/images/expedition-gear/gearImg.png";

/* ================= ITEM PLACEHOLDER IMAGES ================= */
import clothingImg from "../../assets/images/expedition-gear/clothingImg.png";
import mountaineeringImg from "../../assets/images/expedition-gear/mountaineeringImg.png";
import campingImg from "../../assets/images/expedition-gear/campingImg.png";
import safetyImg from "../../assets/images/expedition-gear/safetyImg.png";
import gearChecklistImg from "../../assets/images/expedition-gear/gearChecklist.jpeg";

/* ================= DATA ================= */

const ROUTES = {
  everest: "Everest (8848m)",
  k2: "K2 (8611m)",
  aconcagua: "Trekking (6961m)",
};

/* ================= GEAR LIST DATA ================= */
const SECTIONS = [
  {
    title: "Technical Clothing",
    desc: "Layering systems designed for extreme alpine conditions.",
    image: clothingImg,
    items: [
      "Base Layers (Merino / Synthetic)",
      "Insulated Down Jackets",
      "Hard Shell Jackets & Pants",
      "Expedition Gloves & Mittens",
      "Thermal Socks & Gaiters",
    ],
  },
  {
    title: "Mountaineering Gear",
    desc: "Critical equipment for high-altitude and technical terrain.",
    image: mountaineeringImg,
    items: [
      "Mountaineering Boots (Double / Triple)",
      "Crampons & Ice Axes",
      "Harness, Carabiners, Ascenders",
      "Ropes & Snow Protection",
      "Helmets & Head Protection",
    ],
  },
  {
    title: "Camping & Survival",
    desc: "Self-sufficiency in the harshest environments.",
    image: campingImg,
    items: [
      "High-Altitude Tents",
      "Four-Season Sleeping Bags",
      "Sleeping Mats",
      "Stoves & Fuel Systems",
      "Emergency Shelters",
    ],
  },
  {
    title: "Safety & Navigation",
    desc: "Redundancy and reliability are non-negotiable.",
    image: safetyImg,
    items: [
      "Satellite Phones",
      "GPS Devices & Maps",
      "Avalanche Safety Gear",
      "First Aid & Trauma Kits",
      "Personal Locator Beacons",
    ],
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

/* ================= PAGE ================= */
const downloadGearImage = () => {
  const link = document.createElement("a");
  link.href = gearChecklistImg;
  link.download = "Expedition-Gear-Checklist.png";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default function ExpeditionGearPage() {
  const heroRef = useRef(null);
  const [route, setRoute] = useState("everest");

  return (
    <main className="bg-[#838c91] text-white overflow-x-hidden ">

      {/* ================= HERO ================= */}
      <section
        ref={heroRef}
        className="
          relative w-full
          overflow-hidden
          flex items-end justify-center
        "
          style={{ 
            backgroundImage: `url(${gearBackgroundImg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat", }}
      >

        <div className="relative z-10 w-full px-6 pb-10 pt-32 md:pt-40 lg:pt-40 lg:pb-20">
          <div className="text-center max-w-4xl mx-auto text-[#192c37]">

            {/* CENTER IMAGE */}
            <img
              src={gearImgHero}
              alt="Expedition Gear Icon"
              className="mx-auto mb-6 w-2/3 h-auto"
            />

            <h1 className="text-4xl md:text-5xl font-extrabold">
              Expedition Gear & Preparation
            </h1>

            <p className="mt-6 text-[#192c37]/80">
              Precision-selected equipment for the world’s harshest mountains.
            </p>

            {/* ROUTE SELECTOR */}
            <div className="mt-10 flex justify-center gap-4 flex-wrap">
              {Object.keys(ROUTES).map((r) => (
                <button
                  key={r}
                  onClick={() => setRoute(r)}
                  className={`px-5 py-2 rounded-full border text-sm transition
                    ${
                      route === r
                        ? "bg-[#192c37] text-white"
                        : "border-[#192c37]/20 bg-[#192c37]/10 text-[#192c37]"
                    }`}
                >
                  {ROUTES[r]}
                </button>
              ))}
            </div>

          </div>
        </div>
      </section>




          {/* ================= JOIN / DETAILS ================= */}
      <section className="relative w-full px-6 md:px-10 py-32 md:py-40 bg-slate-900 overflow-hidden">
        {/* BACKGROUND IMAGE */}
        <motion.div
          className="absolute inset-0 bg-cover"
          style={{ backgroundImage: `url(${gearBackgroundImg})` }}
        />
        <div className="absolute inset-0 bg-[#192c37]/50" />

        <div className="relative z-10 max-w-7xl mx-auto space-y-24">

          {/* ================= TOP INFO (CENTERED) ================= */}
          <div className="text-center flex flex-col items-center space-y-6 px-4">
            <p className="text-md tracking-wide text-neutral-200">
              EXPEDITION GEAR
            </p>

            <motion.div
              variants={fadeUp}
              className="
                inline-flex items-center gap-2
                rounded-full
                border border-neutral-300/60
                bg-gray-500/30
                px-3 py-1
                text-xs uppercase tracking-[0.25em]
                text-neutral-200
                backdrop-blur
              "
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Preparation Is Survival
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="
                text-3xl font-semibold tracking-tight
                text-white
                sm:text-4xl md:text-[3rem]
                md:leading-[1.1]
              "
            >
              Built for{" "}
              <span className="bg-gradient-to-r from-neutral-100 to-neutral-100 bg-clip-text text-transparent">
                {ROUTES[route]}
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="max-w-xl text-md leading-relaxed text-neutral-200"
            >
              Technical clothing to survival systems, a complete breakdown of
              expedition essentials used on the mountain.
            </motion.p>

            <motion.div variants={fadeUp}>
              <button
                onClick={downloadGearImage}
                className="
                  rounded-full
                  border border-neutral-300
                  bg-[#192c37]
                  px-8 py-4
                  text-sm font-medium
                  text-white
                  hover:bg-[#192c37]/40
                  transition
                "
              >
                Download Checklist
              </button>
            </motion.div>
          </div>

          {/* ================= GEAR SECTIONS ================= */}
          <div
            className="
              grid grid-cols-1
              lg:grid-cols-2
              gap-16 lg:gap-20
            "
          >
            {SECTIONS.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="
                  flex flex-col
                  items-left
                  text-left
                  space-y-6
                "
              >
                {/* IMAGE */}
                <div className="w-full overflow-hidden rounded-2xl">
                  <img
                    src={section.image}
                    alt={section.title}
                    className="w-full h-auto object-contain"
                  />
                </div>

                {/* TITLE */}
                <h3 className="text-2xl font-bold text-white">
                  {section.title}
                </h3>

                {/* DESCRIPTION */}
                <p className="text-white/70 max-w-md">
                  {section.desc}
                </p>

                {/* GEAR PILLS */}
                <div className="flex flex-wrap  gap-3 pt-2">
                  {section.items.map((item, i) => (
                    <span
                      key={i}
                      className="
                        rounded-full
                        border border-white/15
                        bg-white/5
                        px-4 py-2
                        text-xs font-medium
                        text-white
                        hover:bg-white/10
                        transition
                      "
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </section>
    </main>
  );
}
